import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";

import BlankHeader from "../header/blank_header";
import Sidebar from "../sidebar/sidebar";
import Header from "../header/header";
import colors from "../../design/colors";

/* ----------------------------------
 * TYPES
 * 타입 정의
 ---------------------------------- */
interface LayoutProps {
  children: React.ReactNode;
}

const LayoutContent = ({ children, pathname }: LayoutProps & { pathname?: string }) => {
  /* ----------------------------------
   * GLOBAL STATE
   * 전역 상태 및 전역 변수
   ---------------------------------- */
  const [navigationOpen, setNavigationOpen] = useState(false);
  const closeNavigation = useCallback(() => setNavigationOpen(false), []);

  useEffect(() => {
    setNavigationOpen(false);
  }, [pathname]);

  /* ----------------------------------
   * LOCAL STATE
   * 컴포넌트 내부에서 사용하는 상태
   ---------------------------------- */
  // 로그인 전 화면·약관·사업장 등록은 사이드바 없이 로고 헤더만 둔다.
  const blankHeaderRoutes = ["/login", "/signup", "/find-email", "/reset-password", "/onboarding", "/legal"];
  const showBlankHeader = blankHeaderRoutes.some((path) => pathname?.startsWith(path));
  const showSidebar = !showBlankHeader;

  return (
    <SectionWrapper className={showSidebar ? "cari-workspace" : undefined}>
      <ContentRow className="workspace-row">
        {showSidebar && (
          <Sidebar
            mobileOpen={navigationOpen}
            onClose={closeNavigation}
          />
        )}
        <ContentCol className="workspace-content">
          {showBlankHeader && <BlankHeader />}
          {!showBlankHeader && (
            <Header
              navigationOpen={navigationOpen}
              onToggleNavigation={() => setNavigationOpen((open) => !open)}
            />
          )}
          <main id="main-content" className="main-content">{children}</main>
        </ContentCol>
      </ContentRow>
    </SectionWrapper>
  );
};

const Layout = (props: LayoutProps) => {
  const { pathname } = useLocation();
  return <LayoutContent {...props} pathname={pathname} />;
};

/* ----------------------------------
 * STYLED COMPONENT
 ---------------------------------- */
const SectionWrapper = styled.section`
  width: 100%;
  height: 100%;
  min-height: 0;
  background: ${colors.gray50};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  box-sizing: border-box;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;

  ${({ theme }) => theme.device.mobile} {
    position: fixed;
    height: 100%;
    padding: 0;
  }
`;

const ContentRow = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  box-sizing: border-box;

  ${({ theme }) => theme.device.mobile} {
    flex-direction: column;
    height: 100%;
  }
`;

const ContentCol = styled.div`
  flex: 1 1 0;
  min-width: 0;
  min-height: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  overflow: hidden;
  background-color: ${colors.white};

  .main-content {
    flex: 1 1 0;
    min-width: 0;
    min-height: 0;
    width: 100%;
    background-color: ${colors.gray50};
    position: relative;
    overflow: auto;
    overscroll-behavior: contain;
    padding-bottom: 0;
    ${({ theme }) => theme.device.mobile} {
      padding-bottom: 0;
    }
  }

  ${({ theme }) => theme.device.mobile} {
    width: 100%;
    height: 100%;
    padding: 0;
  }
`;

export default Layout;
